import React from 'react';
import { connect } from 'react-redux';
import { getActiveLanguage, getTranslate } from 'react-localize-redux';
import { serviceLocator } from 'infrastructure/ServiceLocator';
import Lightbox from 'react-images';
import AnchorLink from 'react-anchor-link-smooth-scroll';
import { withStyles, WithStyles, Theme, createStyles } from '@material-ui/core/styles';
import CardGiftcard from '@material-ui/icons/CardGiftcard';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import {
  AppBar,
  Grid,
  Hidden,
  Icon,
  Toolbar,
  Tooltip,
  Typography,
  Paper,
  ExpansionPanel,
  ExpansionPanelDetails,
  ExpansionPanelSummary,
  Link,
} from '@material-ui/core';
import StarBorderIcon from '@material-ui/icons/StarBorder';
import classnames from 'classnames';
import UserHelper from '../../infrastructure/UserHelper';
import moment from 'moment';

const drawerWidth = 300;

const styles = (theme: Theme) =>
  createStyles({
    appBar: {
      marginLeft: 300,
      top: '56px',
      width: '100%',
      opacity: '0.7',
    },
    studioHeader: {
      margin: 0,
      padding: 0,
      listStyle: 'none',
      textAlign: 'center',
      '& > li': {
        display: 'inline-block',
      },
    },
    headerLink: {
      display: 'block',
      color: theme.palette.primary.light,
      transition: 'color 0.25s',
      lineHeight: '1.5',
      padding: theme.spacing(1.5),
      letterSpacing: '0.05rem',
      position: 'relative',
      cursor: 'pointer',
      textDecoration: 'none',
      '&:hover': {
        color: theme.palette.primary.light,
      },
      '& span': {
        position: 'absolute',
        bottom: '100%',
        left: 0,
        right: 0,
        height: 0,
        opacity: 0,
        transition: 'all 0.3s',
      },
      '&:hover span': {
        bottom: '0',
        height: '3px',
        opacity: '1',
      },
    },
    toolbarButtons: {
      marginLeft: 'auto',
      display: 'flex',
      alignItems: 'center',
    },
    drawer: {
      flexShrink: 0,
      [theme.breakpoints.up('md')]: {
        width: drawerWidth,
      },
    },
    avatar: {
      width: theme.spacing(3.5),
      height: theme.spacing(3.5),
    },
    orangeLink: {
      color: theme.palette.primary.main,
      '&:hover': {
        color: theme.palette.primary.main,
      },
    },
    studioMainInfo: {
      minHeight: '100vh',
      padding: '120px 15px 70px',
      boxSizing: 'border-box',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
    },
    infoWrap: {
      padding: theme.spacing(4, 3, 8),
      maxWidth: '740px',
      backgroundColor: 'rgba(255, 255, 255, 0.8)',
      color: theme.palette.primary.dark,
      position: 'relative',
    },
    headerRate: {
      display: 'flex',
      justifyContent: 'flex-start',
      alignItems: 'center',
      margin: theme.spacing(1, 0),
      padding: theme.spacing(1),
      borderRadius: '6px 6px 0 6px',
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.light,
      position: 'absolute',
      bottom: '100%',
      left: 0,
    },
    ratingValue: {
      fontSize: '26px',
      fontWeight: 'bold',
      margin: theme.spacing(0, 2, 0, 0.5),
    },
    reviewCount: {
      textAlign: 'left',
      fontSize: '12px',
      lineHeight: '1.4',
    },
    alertWarning: {
      fontSize: '12px',
      marginTop: theme.spacing(2),
      padding: theme.spacing(1, 2),
      border: '2px solid',
      borderColor: theme.palette.primary.main,
      borderRadius: 0,

      '& > div h1': {
        margin: theme.spacing(0, 0, 2),
      },
      '& > div p': {
        margin: theme.spacing(0, 0, 1),
      },
    },
    infoIcons: {
      padding: theme.spacing(1, 3),
      position: 'absolute',
      bottom: '0',
      left: '0',
      right: '0',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      backgroundColor: 'rgba(30, 32, 36, 0.4)',

      '& > ul': {
        margin: 0,
        padding: 0,
        listStyle: 'none',
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'center',
      },

      '& > ul li': {
        margin: theme.spacing(0, 1, 0, 0),
        width: '32px',
        height: '32px',
        backgroundSize: 'contain',
        backgroundPosition: '50% 50%',
        backgroundRepeat: 'no-repeat',
        position: 'relative',
      },
    },
    promoLink: {
      display: 'flex',
      justifyContent: 'flex-start',
      alignItems: 'center',
      fontWeight: 'bold',
      color: theme.palette.primary.main,
      transition: 'color 0.25s',
      cursor: 'pointer',
      textDecoration: 'none',
      height: '32px',

      '&:hover': {
        color: theme.palette.primary.light,
      },

      '& img': {
        marginRight: theme.spacing(1),
      },
    },
    expansionPanel: {
      backgroundColor: theme.palette.primary.dark,
      color: theme.palette.primary.light,
    },
    icon: {},
  });

interface Notification {
  _id: string;
  text: string;
  [key: string]: any;
}

interface Studio {
  id: string;
  name: string;
  slogan?: string;
  avatar?: string;
  background?: string;
  gallery?: string[];
  ratingValue?: number;
  reviewCount?: number;
  hasSchedule?: boolean;
  hideSchedule?: boolean;
  hasCourses?: boolean;
  hasPricing?: boolean;
  tutoring?: boolean;
  remoteEducation?: boolean;
  inclusive?: boolean;
  specialNeeds?: boolean;
  veteran?: boolean;
  vooptyPromocode?: string;
  [key: string]: any;
}

interface Teacher {
  [key: string]: any;
}

interface Owner {
  [key: string]: any;
}

interface StudioHeaderProps extends WithStyles<typeof styles> {
  studio: Studio;
  teachers?: Teacher[];
  owner?: Owner;
  onTeachersClick?: () => void;
  translate: (key: string, data?: any) => string;
  currentLanguage: string;
  user: any;
  domain: {
    staticUrl: string;
    domain: {
      custom?: boolean;
      blog?: string;
    };
    [key: string]: any;
  };
}

interface StudioHeaderState {
  showSubscribeConfirm: boolean;
  showSubscribeConfirmed: boolean;
  showUnsubscribeConfirm: boolean;
  showUnsubscribeConfirmed: boolean;
  showRegisterToClass: boolean;
  studio: Studio;
  navClass: string;
  notifications?: Notification[];
  showMenu?: boolean;
  lightboxIsOpen?: boolean;
  currentImage?: number;
}

class StudioHeader extends React.Component<StudioHeaderProps, StudioHeaderState> {
  private delta = 5;
  private navbarHeight = 56;
  private headerHeight = 56;

  constructor(props: StudioHeaderProps) {
    super(props);
    this.state = {
      showSubscribeConfirm: false,
      showSubscribeConfirmed: false,
      showUnsubscribeConfirm: false,
      showUnsubscribeConfirmed: false,
      showRegisterToClass: false,
      studio: this.props.studio,
      navClass: 'studio-header-full',
    };
  }

  componentDidMount() {
    let api = serviceLocator.notificationApi;
    api.getNotificationsById(this.state.studio.id).then(
      (notifications: Notification[]) => {
        this.setState({ notifications: notifications });
      },
      (err) => {
        if (err) {
          alert(err);
        }
      }
    );
  }

  renderRating = (studio: Studio) => {
    if (studio.ratingValue) {
      return (
        <div className={this.props.classes.headerRate}>
          <StarBorderIcon />
          <span className={this.props.classes.ratingValue}>{Math.round(studio.ratingValue * 10) / 10}</span>
          <span className={this.props.classes.reviewCount}>
            {this.renderRatingLabel(studio.ratingValue)}
            <br />
            {studio.reviewCount} {this.props.translate('studio.reviewsNumber')}
          </span>
        </div>
      );
    }
    return null;
  };

  renderRatingLabel = (ratingValue: number) => {
    if (ratingValue > 4) {
      return this.props.translate('studio.review.great');
    }
    if (ratingValue > 3) {
      return this.props.translate('studio.review.good');
    }
    if (ratingValue > 2) {
      return this.props.translate('studio.review.ok');
    }
    if (ratingValue > 1) {
      return this.props.translate('studio.review.notGood');
    }
    return this.props.translate('studio.review.bad');
  };

  renderToolBar = () => {
    const { classes } = this.props;
    return (
      <AppBar position="fixed" color="secondary" className={classes.appBar}>
        <Hidden mdUp>
          <ExpansionPanel
            square
            expanded={this.state.showMenu}
            className={classes.expansionPanel}
            onChange={() => {
              this.setState({ showMenu: !this.state.showMenu });
            }}
          >
            <ExpansionPanelSummary expandIcon={<ExpandMoreIcon style={{ color: 'white' }} />}>
              <Typography align="center">{this.state.studio.name}</Typography>
            </ExpansionPanelSummary>
            <ExpansionPanelDetails>{this.renderMenu()}</ExpansionPanelDetails>
          </ExpansionPanel>
        </Hidden>
        <Hidden smDown>{this.renderMenu()}</Hidden>
      </AppBar>
    );
  };

  renderMenu = () => {
    const { classes } = this.props;
    return (
      <ul className={classes.studioHeader}>
        <li>
          <AnchorLink href="#aboutus" offset={() => 50} className={classes.headerLink}>
            <span style={{ background: `orange` }}></span>
            {this.props.translate('studio.aboutUs')}
          </AnchorLink>
        </li>
        {this.renderTeachersLink()}
        {this.renderCoursesLink()}
        {this.renderScheduleCalendarLink()}
        {this.renderPricingLink()}
        {this.renderReviews()}
        {this.renderBlog()}
        {this.renderContacts()}
        {this.renderTermsAndConditions()}
      </ul>
    );
  };

  renderPromocode = () => {
    if (!this.props.domain.domain.custom && this.state.studio.vooptyPromocode) {
      return (
        <div>
          <AnchorLink href="#vooptyDiscount" offset={() => 100} className={this.props.classes.promoLink}>
            <Tooltip title={this.props.translate('studio.promocode')}>
              <img
                src={`${this.props.domain.staticUrl}/percentage.svg`}
                height={32}
                alt={this.props.translate('studio.promocode')}
                width={32}
              />
            </Tooltip>
            <Hidden smDown>{this.props.translate('studio.promocode')}</Hidden>
          </AnchorLink>
        </div>
      );
    }
    return null;
  };

  render = () => {
    let studio = this.state.studio;
    let divStyle = {
      backgroundSize: 'cover',
      position: 'relative' as const,
      backgroundPosition: 'center center',
    };
    let avatarUrl = studio.avatar;
    return (
      <div
        className={classnames('lazyload studioHead ' + this.state.navClass)}
        data-bgset={studio.background ? studio.background : `url(${this.props.domain.staticUrl}/studioDefaultBG.png)`}
        style={divStyle}
        id="studio-big-header"
      >
        <div className={this.props.classes.studioMainInfo}>
          <div className={this.props.classes.infoWrap}>
            <Grid container spacing={3} justify="flex-start" alignItems="center">
              <Grid item>
                {avatarUrl && (
                  <img src={avatarUrl} alt={studio.name} className="lazyload" style={{ maxWidth: '200px' }} />
                )}
              </Grid>
              <Grid item>
                <Typography variant="h3" component="h1" align="left">
                  {studio.name}
                </Typography>
                <Typography variant="h6" component="h2" align="left">
                  {studio.slogan}
                </Typography>
              </Grid>
            </Grid>
            {this.renderNotificationBlok()}
            {this.renderRating(studio)}
            <div className={this.props.classes.infoIcons}>
              <ul>
                {this.renderTutoring(studio.tutoring)}
                {this.renderRemoteEducation(studio.remoteEducation)}
                {this.renderInclusive(studio.inclusive)}
                {this.renderSpecialNeeds(studio.specialNeeds)}
                {this.renderVeteran(studio.veteran)}
              </ul>
              {this.renderPromocode()}
            </div>
          </div>
        </div>
        {this.renderToolBar()}
      </div>
    );
  };

  renderBlog = () => {
    if (this.props.domain.domain.custom && this.props.domain.domain.blog) {
      return (
        <li>
          <a
            href={this.props.domain.domain.blog}
            className={this.props.classes.headerLink}
            target="_blank"
            rel="noopener noreferrer"
          >
            <span style={{ background: `orange` }}></span>
            {this.props.translate('common.blog')}
          </a>
        </li>
      );
    }
    return null;
  };

  renderContacts = () => {
    return (
      <li>
        <AnchorLink href="#studio-contacts" className={this.props.classes.headerLink} offset={() => 70}>
          <span style={{ background: `orange` }}></span>
          {this.props.translate('studio.contacts')}
        </AnchorLink>
      </li>
    );
  };

  renderTermsAndConditions = () => {
    if (!this.props.owner) return null;

    const urlTermsAndConditions = UserHelper.getTermsAndConditions(this.props.owner);
    if (!urlTermsAndConditions) {
      return null;
    }
    return (
      <li>
        <Link href={urlTermsAndConditions} className={this.props.classes.headerLink}>
          <span style={{ background: `orange` }}></span>
          {this.props.translate('common.offerAgreement')}
        </Link>
      </li>
    );
  };

  renderReviews = () => {
    return (
      <li>
        <AnchorLink href="#reviews" className={this.props.classes.headerLink} offset={() => 50}>
          <span style={{ background: `orange` }}></span>
          {this.props.translate('studio.reviews')}
        </AnchorLink>
      </li>
    );
  };

  renderScheduleCalendarLink = () => {
    if (this.state.studio.hasSchedule && !this.state.studio.hideSchedule) {
      return (
        <li>
          <AnchorLink href="#schedule-calendar" className={this.props.classes.headerLink} offset={() => 50}>
            <span style={{ background: `orange` }}></span>
            {this.props.translate('studio.schedule')}
          </AnchorLink>
        </li>
      );
    }
    return null;
  };

  renderTeachersLink = () => {
    if (this.props.teachers && this.props.teachers.length > 0) {
      return (
        <li>
          <AnchorLink
            href="#teachers"
            className={this.props.classes.headerLink}
            onClick={() => {
              if (this.props.onTeachersClick) {
                this.props.onTeachersClick();
              }
            }}
            offset={() => 50}
          >
            <span style={{ background: `orange` }}></span>
            {this.props.translate('studio.teachers')}
          </AnchorLink>
        </li>
      );
    }
    return null;
  };

  renderCoursesLink = () => {
    if (this.state.studio.hasCourses) {
      return (
        <li>
          <AnchorLink href="#schedule" className={this.props.classes.headerLink} offset={() => 50}>
            <span style={{ background: `orange` }}></span>
            {this.props.translate('studio.courses')}
          </AnchorLink>
        </li>
      );
    }
    return null;
  };

  renderPricingLink = () => {
    if (this.state.studio.hasPricing) {
      return (
        <li>
          <AnchorLink href="#price" className={this.props.classes.headerLink} offset={() => 50}>
            <span style={{ background: `orange` }}></span>
            {this.props.translate('studio.pricing')}
          </AnchorLink>
        </li>
      );
    }
    return null;
  };

  renderGallery = () => {
    if (!this.state.studio.gallery) return null;

    let gallery: { src: string }[] = [];
    for (let photoIndex in this.state.studio.gallery) {
      let photoUrl = this.state.studio.gallery[photoIndex];
      gallery.push({ src: photoUrl });
    }
    if (gallery.length > 0) {
      return (
        <div>
          <span onClick={this.openLightbox} className="view-gallery-btn ">
            {this.props.translate('studio.viewPhotos')}
          </span>
          {this.renderLightbox(gallery)}
        </div>
      );
    }
    return null;
  };

  renderLightbox = (gallery: { src: string }[]) => {
    if (this.state.lightboxIsOpen) {
      return (
        <Lightbox
          images={gallery}
          onClose={this.closeLightbox}
          onClickPrev={this.gotoPrevious}
          onClickNext={this.gotoNext}
          currentImage={this.state.currentImage || 0}
          isOpen={this.state.lightboxIsOpen}
        />
      );
    }
    return null;
  };

  openLightbox = () => {
    this.setState({
      currentImage: 0,
      lightboxIsOpen: true,
    });
  };

  closeLightbox = () => {
    this.setState({
      currentImage: 0,
      lightboxIsOpen: false,
    });
  };

  gotoPrevious = () => {
    this.setState({
      currentImage: (this.state.currentImage || 0) - 1,
    });
  };

  gotoNext = () => {
    this.setState({
      currentImage: (this.state.currentImage || 0) + 1,
    });
  };

  renderNotificationBlok = () => {
    if (this.state.notifications) {
      return this.renderNotifications();
    }
    return null;
  };

  renderNotifications = () => {
    if (!this.state.notifications) return null;

    return <div>{this.state.notifications.map((item) => this.renderNotification(item))}</div>;
  };

  renderNotification = (notification: Notification) => {
    return (
      <Paper className={this.props.classes.alertWarning} key={'notification-' + notification._id}>
        <div dangerouslySetInnerHTML={{ __html: notification.text }} />
      </Paper>
    );
  };

  renderTutoring = (tutoring?: boolean) => {
    if (tutoring) {
      return (
        <Tooltip title={this.props.translate('studio.tutoring')}>
          <li>
            <Icon className={this.props.classes.icon}>
              <img
                src={`${this.props.domain.staticUrl}/professor.svg`}
                alt={this.props.translate('studio.tutoring')}
                height={32}
                width={32}
              />
            </Icon>
          </li>
        </Tooltip>
      );
    }
    return null;
  };

  renderRemoteEducation = (remoteEducation?: boolean) => {
    if (remoteEducation) {
      return (
        <Tooltip title={this.props.translate('studio.remoteEducation')}>
          <li>
            <Icon className={this.props.classes.icon}>
              <img
                src={`${this.props.domain.staticUrl}/worldwide.svg`}
                alt={this.props.translate('studio.remoteEducation')}
                height={32}
                width={32}
              />
            </Icon>
          </li>
        </Tooltip>
      );
    }
    return null;
  };

  renderSpecialNeeds = (specialNeeds?: boolean) => {
    if (specialNeeds) {
      return (
        <Tooltip title={this.props.translate('studio.specialNeeds')}>
          <li>
            <Icon className={this.props.classes.icon}>
              <img
                src={`${this.props.domain.staticUrl}/wheelchair.svg`}
                alt={this.props.translate('studio.specialNeeds')}
                height={32}
                width={32}
              />
            </Icon>
          </li>
        </Tooltip>
      );
    }
    return null;
  };

  renderVeteran = (veteran?: boolean) => {
    if (veteran) {
      return (
        <Tooltip title={this.props.translate('studio.veteran')}>
          <li></li>
        </Tooltip>
      );
    }
    return null;
  };

  renderInclusive = (inclusive?: boolean) => {
    if (inclusive) {
      return (
        <Tooltip title={this.props.translate('studio.inclusive')}>
          <li>
            <Icon className={this.props.classes.icon}>
              <img
                src={`${this.props.domain.staticUrl}/creative.svg`}
                alt={this.props.translate('studio.inclusive')}
                height={32}
                width={32}
              />
            </Icon>
          </li>
        </Tooltip>
      );
    }
    return null;
  };
}

const mapStateToProps = (state: any) => ({
  translate: getTranslate(state.locale),
  currentLanguage: getActiveLanguage(state.locale).code,
  user: state.user,
  domain: state.domain,
});

const mapDispatchToProps = () => {
  return {};
};

export default withStyles(styles)(connect(mapStateToProps, mapDispatchToProps)(StudioHeader));
